/**
 * CockpitTile — compact executive signal tile.
 * Shows label, primary value, secondary line, status chip, and drill-through.
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

export type TileStatus = 'ok' | 'warn' | 'critical' | 'na';

export interface CockpitTileProps {
  label: string;
  /** Primary display value (number, date string, or formatted text) */
  value: string | number | null;
  /** Secondary descriptive line */
  secondary?: string;
  status?: TileStatus;
  /** Small chips shown below secondary line */
  chips?: string[];
  /** CTA shown when data missing */
  ctaLabel?: string;
  ctaHref?: string;
  onClick?: () => void;
  className?: string;
}

const STATUS_BG: Record<TileStatus, string> = {
  ok: 'border-l-emerald-500',
  warn: 'border-l-amber-500',
  critical: 'border-l-red-500',
  na: 'border-l-border',
};

const STATUS_BADGE: Record<TileStatus, 'ok' | 'warn' | 'critical' | 'outline'> = {
  ok: 'ok',
  warn: 'warn',
  critical: 'critical',
  na: 'outline',
};

const STATUS_LABEL: Record<TileStatus, string> = {
  ok: 'OK',
  warn: 'Warn',
  critical: 'Critical',
  na: 'N/A',
};

export const CockpitTile: React.FC<CockpitTileProps> = ({
  label,
  value,
  secondary,
  status = 'na',
  chips,
  ctaLabel,
  ctaHref,
  onClick,
  className,
}) => {
  const isNA = value === null || value === undefined;

  return (
    <div
      className={cn(
        'border rounded-lg bg-card p-3 border-l-4 flex flex-col gap-1 min-w-0',
        STATUS_BG[status],
        onClick && 'cursor-pointer hover:bg-muted/50 transition-colors',
        className,
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {/* Label row */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium text-muted-foreground truncate leading-tight">
          {label}
        </span>
        <Badge variant={STATUS_BADGE[status]} size="sm">
          {STATUS_LABEL[status]}
        </Badge>
      </div>

      {/* Value */}
      <span
        className={cn(
          'text-lg font-semibold leading-tight break-words',
          isNA ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {isNA ? '—' : value}
      </span>

      {/* Secondary — allow wrap so full text is visible */}
      {secondary && (
        <span className="text-[11px] text-muted-foreground leading-tight whitespace-normal break-words min-w-0">
          {secondary}
        </span>
      )}

      {/* Chips */}
      {chips && chips.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-0.5">
          {chips.map((c) => (
            <span
              key={c}
              className="text-[10px] px-1.5 py-px rounded bg-muted text-muted-foreground border border-border leading-tight"
            >
              {c}
            </span>
          ))}
        </div>
      )}

      {/* CTA for missing data */}
      {isNA && ctaLabel && (
        <a
          href={ctaHref ?? '#'}
          onClick={(e) => e.stopPropagation()}
          className="text-[11px] text-primary hover:underline mt-1"
        >
          {ctaLabel}
        </a>
      )}
    </div>
  );
};
